/**
 * 视频懒加载指令
 * - 进入视口自动播放，离开视口暂停并释放内存
 * - 保留 poster 属性作为初始封面，离开视口时恢复显示
 * - 兼容低版本 Safari（不支持 IntersectionObserver 时使用 scroll 事件回退）
 * - 兼容夸克/UC等国产浏览器的视频劫持问题
 */
export const vLazyVideo = {
  mounted(el: HTMLVideoElement) {
    // 确保只在客户端执行
    if (import.meta.server) return

    const originalSrc = el.dataset.src || ''
    if (!originalSrc) return

    // 保存原始 poster 属性
    const originalPoster = el.getAttribute('poster') || ''

    // 添加兼容国产浏览器的属性，防止视频被劫持为小窗播放
    // 夸克/UC浏览器
    el.setAttribute('x5-video-player-type', 'h5-page')
    el.setAttribute('x5-video-player-fullscreen', 'false')
    el.setAttribute('x5-video-orientation', 'portraint')
    // 微信浏览器
    el.setAttribute('x5-playsinline', 'true')
    // iOS
    el.setAttribute('webkit-playsinline', 'true')
    el.setAttribute('playsinline', 'true')
    // 禁用画中画
    el.setAttribute('disablePictureInPicture', 'true')
    // 禁用远程播放
    el.setAttribute('disableRemotePlayback', 'true')

    // 状态标志，避免重复操作
    let isLoaded = false
    let isPlaying = false

    // 检查元素是否在视口内
    const isInViewport = () => {
      const rect = el.getBoundingClientRect()
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight
      const windowWidth =
        window.innerWidth || document.documentElement.clientWidth
      return (
        rect.bottom >= -100 &&
        rect.right >= 0 &&
        rect.top <= windowHeight + 100 &&
        rect.left <= windowWidth
      )
    }

    // 处理视频加载和播放
    const handleVisibility = () => {
      if (isInViewport()) {
        if (!isLoaded) {
          el.src = originalSrc
          el.load()
          isLoaded = true
        }
        if (!isPlaying) {
          el.play().catch(() => {})
          isPlaying = true
        }
      } else if (isLoaded || isPlaying) {
        el.pause()
        el.currentTime = 0
        el.removeAttribute('src')
        el.load()
        // 离开视口时恢复 poster
        if (originalPoster) {
          el.setAttribute('poster', originalPoster)
        }
        isLoaded = false
        isPlaying = false
      }
    }

    // 检查 IntersectionObserver 支持
    const hasIntersectionObserver =
      typeof window !== 'undefined' && 'IntersectionObserver' in window

    if (hasIntersectionObserver) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!isLoaded) {
                el.src = originalSrc
                el.load()
                isLoaded = true
              }
              if (!isPlaying) {
                el.play().catch(() => {})
                isPlaying = true
              }
            } else if (isLoaded || isPlaying) {
              el.pause()
              el.currentTime = 0
              el.removeAttribute('src')
              el.load()
              // 离开视口时恢复 poster
              if (originalPoster) {
                el.setAttribute('poster', originalPoster)
              }
              isLoaded = false
              isPlaying = false
            }
          })
        },
        { threshold: 0.1, rootMargin: '0px' },
      )

      observer.observe(el)
      ;(el as any)._lazyObserver = observer
    } else {
      // 回退方案：使用 scroll 事件（低版本 Safari）
      let ticking = false
      const scrollHandler = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            handleVisibility()
            ticking = false
          })
          ticking = true
        }
      }

      handleVisibility()
      window.addEventListener('scroll', scrollHandler, { passive: true })
      window.addEventListener('resize', scrollHandler, { passive: true })
      ;(el as any)._scrollHandler = scrollHandler
    }
  },
  beforeUnmount(el: HTMLVideoElement) {
    // 确保只在客户端执行
    if (import.meta.server) return

    if ((el as any)._lazyObserver) {
      ;(el as any)._lazyObserver.disconnect()
    }
    if ((el as any)._scrollHandler) {
      window.removeEventListener('scroll', (el as any)._scrollHandler)
      window.removeEventListener('resize', (el as any)._scrollHandler)
    }
    el.pause()
    el.removeAttribute('src')
    el.load()
  },
}
